// Catalog-only half of the device analytics context, split out of device-context.ts
// so non-UI emit sites can resolve brand/model without pulling the UI graph.
//
// device-context.ts needs locateProxy/locateDevice for the agent- and room-scoped
// fields, and '#~/agent/utils.js' reaches a React component five hops later
// (agent/utils -> agent/broker -> media-devices/pipeline -> video/components/editor/local
// -> @studio/design-system/select -> @radix-ui/react-select). Any module that imports
// device-context.ts inherits that whole subgraph. When such a module also sits under
// the '#~/posthog/index.js' barrel, every jest suite reaching that barrel links the
// UI graph too, and jest's --experimental-vm-modules linker then fails on the CJS-only
// deps in it ("@radix-ui/react-select does not provide an export named 'SelectPortal'")
// in suites that render nothing. Same reason calibration-lifecycle, boot-lifecycle and
// feature-gate are kept out of the analytics/domains barrel.
//
// Everything here depends only on depot, the group-identify cache and the PostHog
// manager module — none of which reach the UI graph. Import PostHogManager from the
// '#~/posthog/manager.js' leaf, never the '#~/posthog/index.js' barrel: the barrel
// re-exports the encoder-error surface, so importing it would add a DIRECT edge back
// into this file. The leaf avoids only that direct edge — a cycle still closes through
// longer routes (depot and the posthog barrel both re-enter encoder-errors, which
// imports this module). That remaining cycle is not a live defect: every value crossing
// it — the depot and PostHogManager reads here, and encoder-errors' call to
// resolveDeviceAnalyticsCatalogContext — is consumed lazily inside a function body,
// never at module top level, so the ESM live bindings are already resolved by call time.
// Verified empirically: full jest green (both new encoder-error suites exercise this
// path) and `vite build --mode=integration` exits 0 with no Rollup circular warning.
// Nothing in CI guards this — `no-circular` is severity:'warn' and no workflow runs
// madge or depcruise.
import type { Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { depot } from '#~/depot/index.js';
import { lookupRegistryTemplate } from '#~/device/registry/registry-config-cache.js';
import { PostHogManager } from '#~/posthog/manager.js';
import { groupIdentifyCache } from '#~/analytics/utils/group-identify-cache.js';

// Per ENG-3960 §E12 — canonical device-cohort shape: IDs power cohort breakdowns,
// *_name props the labels. Replaces the pre-ENG-3960 device_brand/device_model keys.
export type DeviceAnalyticsCatalogContext = {
  brand_name?: string;
  model_name?: string;
  device_brand_id?: string;
  device_model_id?: string;
};

export type DeviceAnalyticsCatalogModelLike = {
  id?: string | number;
  name?: string;
  brandId?: string | number;
  brandName?: string;
  brand?: {
    name?: string;
  };
};

const resolveRegistryModel = (modelId: T_Maybe<Model['id']>): T_Maybe<Model> => {
  if (!modelId) {
    return undefined;
  }

  const { registry } = depot.getState();
  const templateModel = lookupRegistryTemplate(registry.templates, modelId)?.model;

  if (templateModel) {
    return templateModel;
  }

  for (const collection of Object.values(registry.models)) {
    const model = collection?.items?.[modelId];

    if (model) {
      return model;
    }
  }

  return undefined;
};

export const resolveDeviceAnalyticsCatalogContext = (
  modelId: T_Maybe<Model['id']>
): DeviceAnalyticsCatalogContext => {
  if (!modelId) {
    return {};
  }

  const model = resolveRegistryModel(modelId);

  if (!model) {
    return {
      device_model_id: modelId
    };
  }

  const brand = depot.getState().registry.brands?.items?.[model.brandId];

  if (brand && model.brandId && !groupIdentifyCache.has('camera_brand', model.brandId)) {
    const brandIdentified = PostHogManager.getInstance().group('camera_brand', model.brandId, {
      name: brand.name,
      website: ''
    });

    if (brandIdentified) {
      groupIdentifyCache.add('camera_brand', model.brandId);
    }
  }

  const cameraModelCacheKey = model.id;

  if (!groupIdentifyCache.has('camera_model', cameraModelCacheKey)) {
    const modelIdentified = PostHogManager.getInstance().group('camera_model', model.id, {
      name: model.name ?? '',
      device_brand_id: model.brandId ?? '',
      brand_name: brand?.name ?? '',
      category: ''
    });

    if (modelIdentified) {
      groupIdentifyCache.add('camera_model', cameraModelCacheKey);
    }
  }

  // Per ENG-3960 §E12 — emit canonical cohort props only. device_model_id is always
  // present here; device_brand_id auto-attaches whenever the model resolves a brand,
  // guaranteeing the §F structural cohort coverage without per-call-site wiring.
  return {
    ...(brand?.name ? { brand_name: brand.name } : {}),
    ...(model.name ? { model_name: model.name } : {}),
    ...(model.brandId ? { device_brand_id: model.brandId } : {}),
    device_model_id: model.id
  };
};

const toAnalyticsId = (id: T_Maybe<string | number | Model['id']>): T_Maybe<Model['id']> => {
  if (id === undefined || id === null || id === '') {
    return undefined;
  }

  return String(id) as Model['id'];
};

export const resolveDeviceAnalyticsCatalogContextFromModel = (
  model: T_Maybe<DeviceAnalyticsCatalogModelLike>,
  fallbackModelId?: T_Maybe<string | number | Model['id']>
): DeviceAnalyticsCatalogContext => {
  const resolvedModelId = toAnalyticsId(model?.id ?? fallbackModelId);
  const registryContext = resolveDeviceAnalyticsCatalogContext(resolvedModelId);
  const fallbackBrandName = model?.brandName ?? model?.brand?.name;

  // Per ENG-3960 §E12 — registry-derived brand/model names win. The selected
  // wizard model is only a fallback when registry hydration has not completed yet.
  return {
    ...registryContext,
    ...(model?.brandId && !registryContext.device_brand_id
      ? { device_brand_id: String(model.brandId) }
      : {}),
    ...(model?.name && !registryContext.model_name ? { model_name: model.name } : {}),
    ...(fallbackBrandName && !registryContext.brand_name ? { brand_name: fallbackBrandName } : {})
  };
};
